import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./Contact.css";

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState("");
  const formRef = useRef(null);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError("");
  };

  const closeSuccessAlert = () => {
    setIsSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!captchaToken) {
      setError("Please verify you're not a robot.");
      setIsLoading(false);
      return;
    }

    const formData = new FormData(formRef.current);
    const data = Object.fromEntries(formData.entries());
    data["g-recaptcha-response"] = captchaToken;

    try {
      const response = await fetch("https://formspree.io/f/mdaprwab", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Form submission failed.");
      }

      setIsSubmitted(true);
      formRef.current.reset();
      setCaptchaToken(null);

      const recaptchaIframe = document.querySelector('iframe[title="reCAPTCHA"]');
      if (recaptchaIframe && window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } catch (err) {
      setError("An error occurred while sending your message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="cis-contact" id="contact" aria-label="Contact us">
      <div className="cis-contact__container">
        <div className="cis-contact__header">
          <h2 className="cis-contact__title">
            Get in<span className="accent-text"> touch</span>
          </h2>
        </div>

        <div className="cis-contact__content">
          <div className="cis-contact__info">
            <h3 className="cis-contact__info-title">Visit or reach us</h3>

            <div className="cis-contact__info-item">
              <span className="material-icons-outlined cis-contact__info-icon">
                location_on
              </span>
              <div className="cis-contact__info-text">
                <h4>Office Address</h4>
                <p>
                  Community Impact Solutions LLC
                  <br />
                  P.O. Box 950
                  <br />
                  Tacoma, WA 98401
                  <br />
                  United States
                </p>
              </div>
            </div>

            <div className="cis-contact__info-item">
              <span className="material-icons-outlined cis-contact__info-icon">
                phone
              </span>
              <div className="cis-contact__info-text">
                <h4>Call Us</h4>
                <p>+1 253 230 3558</p>
              </div>
            </div>

            <div className="cis-contact__info-item">
              <span className="material-icons-outlined cis-contact__info-icon">
                email
              </span>
              <div className="cis-contact__info-text">
                <h4>Email Us</h4>
                <p>nelson@communityimpactsolutionsllc.com</p>
              </div>
            </div>

            <div className="cis-contact__info-item">
              <span className="material-icons-outlined cis-contact__info-icon">
                schedule
              </span>
              <div className="cis-contact__info-text">
                <h4>Hours</h4>
                <p>
                  Monday to Friday
                  <br />
                  9:00 AM - 5:00 PM
                </p>
              </div>
            </div>

            <div className="cis-contact__social">
              <div className="cis-contact__social-links"></div>
            </div>
          </div>

          <div className="cis-contact__form-container">
            <h3 className="cis-contact__form-title">Send us a message</h3>

            {error && (
              <div className="cis-contact__error-message" role="alert">
                {error}
              </div>
            )}

            <form
              className="cis-contact__form"
              onSubmit={handleSubmit}
              ref={formRef}
            >
              <input
                type="text"
                name="_gotcha"
                className="cis-contact__honeypot"
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="cis-contact__form-row">
                <div className="cis-contact__form-group">
                  <label htmlFor="firstName" className="cis-contact__label">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="cis-contact__input"
                    placeholder="John"
                    required
                  />
                </div>

                <div className="cis-contact__form-group">
                  <label htmlFor="lastName" className="cis-contact__label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="cis-contact__input"
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="cis-contact__form-group">
                <label htmlFor="email" className="cis-contact__label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="cis-contact__input"
                  placeholder="john.doe@example.com"
                  required
                />
              </div>

              <div className="cis-contact__form-group">
                <label htmlFor="phone" className="cis-contact__label">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="cis-contact__input"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div className="cis-contact__form-group">
                <label htmlFor="subject" className="cis-contact__label">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="cis-contact__input"
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <div className="cis-contact__form-group">
                <label htmlFor="message" className="cis-contact__label">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="cis-contact__textarea"
                  placeholder="Tell us about your project..."
                  rows="5"
                  required
                ></textarea>
              </div>

              <div className="cis-contact__recaptcha">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  theme="light"
                />
              </div>

              <button
                type="submit"
                className="cis-contact__submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <span className="material-icons-outlined cis-contact__submit-icon">
                      send
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {isSubmitted && (
        <div className="cis-contact__alert-backdrop" onClick={closeSuccessAlert}>
          <div
            className="cis-contact__alert-box"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="success-alert-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cis-contact__alert-icon-wrap">
              <span className="material-icons-outlined cis-contact__alert-icon">
                check_circle
              </span>
            </div>

            <h4 id="success-alert-title" className="cis-contact__alert-title">
              Message sent successfully
            </h4>

            <p className="cis-contact__alert-text">
              Thank you for reaching out. Your message has been sent successfully.
            </p>

            <button
              type="button"
              className="cis-contact__alert-button"
              onClick={closeSuccessAlert}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;